/**
 * Walk the freshly-inserted synthesis blocks and replace key sections
 * (TL;DR, Conflicts & Gaps, Open Questions, Next Steps) with
 * `affine:callout` blocks so they pop visually for the researcher.
 *
 * Why post-insertion instead of markdown-level transformation?
 * BlockSuite's markdown adapter has no callout output — emitting
 * callout-flavour blocks from markdown is not supported. So we let the
 * adapter produce its standard heading + paragraph models, then walk
 * the result and reparent matched sections under a callout.
 *
 * The Sources section (`### 📚 Sources`) is intentionally left alone —
 * researchers expect a real bibliography heading there, not a callout.
 */
import type { Store } from '@blocksuite/affine/store';

interface SectionRule {
  emoji: string;
  pattern: RegExp;
}

const SECTION_RULES: SectionRule[] = [
  // TL;DR — surfaces the headline finding
  { emoji: '💡', pattern: /^\s*tl;?\s*dr\b/i },
  // Conflicts / consistency issues — the most load-bearing scan target
  { emoji: '⚠️', pattern: /^\s*(conflicts?\s*(&|and)\s*gaps?|consistency\s*check)\b/i },
  // Open questions — what's still unresolved
  { emoji: '❓', pattern: /^\s*open\s*questions?\b/i },
  // Next steps — what to do with the findings
  { emoji: '🎯', pattern: /^\s*(next\s*steps|next\s*directions|suggested\s*follow[-\s]?ups?)\b/i },
];

interface ParagraphModelLike {
  flavour: string;
  id: string;
  type?: string;
  text?: { toString(): string; insert(value: string, index: number): void };
  children?: ParagraphModelLike[];
}

function modelText(model: ParagraphModelLike | null | undefined): string {
  try {
    return model?.text?.toString().trim() ?? '';
  } catch {
    return '';
  }
}

function isHeading(model: ParagraphModelLike): boolean {
  return (
    model.flavour === 'affine:paragraph' &&
    (model.type === 'h1' ||
      model.type === 'h2' ||
      model.type === 'h3' ||
      model.type === 'h4')
  );
}

function matchSection(model: ParagraphModelLike): SectionRule | null {
  if (!isHeading(model)) return null;
  const text = modelText(model);
  if (!text) return null;
  for (const rule of SECTION_RULES) {
    if (rule.pattern.test(text)) return rule;
  }
  return null;
}

/**
 * Transform sections inside `blockIds` into callout blocks.
 *
 * `runWith` should wrap the work in the same transactional context the
 * doc-writer uses (`runWithWrites` / `safeTransact`) so the doc's
 * readonly flag is respected and CRDT writes are batched.
 */
export function sectionsToCallouts(
  doc: Store,
  blockIds: ReadonlyArray<string>,
  runWith: (fn: () => void) => void
): void {
  if (!blockIds.length) return;

  // Resolve models once. Filter out IDs that don't resolve (block may
  // have been removed by an earlier transformation).
  const models = blockIds
    .map(id => doc.getBlock(id)?.model as ParagraphModelLike | undefined)
    .filter((m): m is ParagraphModelLike => !!m);

  if (!models.length) return;

  // Identify sections in pass 1 (no doc writes yet); apply in pass 2.
  // Each match captures: heading model + body models that follow until
  // the next heading-level block or the end of the inserted range.
  interface Match {
    rule: SectionRule;
    headingId: string;
    bodyIds: string[];
    headingText: string;
  }
  const matches: Match[] = [];
  for (let i = 0; i < models.length; i++) {
    const model = models[i];
    const rule = matchSection(model);
    if (!rule) continue;
    const headingId = model.id;
    const headingText = modelText(model);
    const bodyIds: string[] = [];
    for (let j = i + 1; j < models.length; j++) {
      const next = models[j];
      if (isHeading(next)) break;
      bodyIds.push(next.id);
      i = j; // advance outer pointer past the consumed body blocks
    }
    matches.push({ rule, headingId, bodyIds, headingText });
  }

  if (!matches.length) return;

  runWith(() => {
    for (const match of matches) {
      try {
        applyCallout(doc, match);
      } catch (err) {
        // One bad section shouldn't kill the rest.
        console.warn('[sections-to-callouts] failed for section:', err);
      }
    }
  });
}

function applyCallout(
  doc: Store,
  match: { rule: SectionRule; headingId: string; bodyIds: string[]; headingText: string }
): void {
  const heading = doc.getBlock(match.headingId)?.model as
    | ParagraphModelLike
    | undefined;
  if (!heading) return;
  const parentId = (heading as unknown as { parent?: { id: string } }).parent
    ?.id;
  if (!parentId) return;
  const parent = doc.getBlock(parentId)?.model as
    | ParagraphModelLike
    | undefined;
  if (!parent || !parent.children) return;

  const headingIndex = parent.children.findIndex(c => c.id === match.headingId);
  if (headingIndex < 0) return;

  // Snapshot the body text BEFORE deleting so we can re-insert as
  // children of the callout. We treat list items by prefixing with a
  // bullet — the markdown adapter renders affine:list as block-level,
  // and callout's child whitelist only allows paragraphs, so we
  // flatten everything to plain paragraph text. Bold / italic / links
  // are preserved by reading `text.toString()` (which keeps the raw
  // characters including the markdown link syntax that the original
  // synthesis produced).
  const bodyTexts: string[] = [];
  for (const id of match.bodyIds) {
    const m = doc.getBlock(id)?.model as ParagraphModelLike | undefined;
    if (!m) continue;
    const t = modelText(m);
    if (!t) continue;
    if (m.flavour === 'affine:list') {
      bodyTexts.push(`• ${t}`);
    } else {
      bodyTexts.push(t);
    }
  }

  // Delete heading + body in reverse order to keep indices stable.
  const toDelete = [match.headingId, ...match.bodyIds];
  for (const id of toDelete.slice().reverse()) {
    const m = doc.getBlock(id)?.model;
    if (m) {
      try {
        doc.deleteBlock(m);
      } catch {
        /* already gone */
      }
    }
  }

  // Insert the callout at the heading's former position. The callout
  // schema uses { emoji, text } props.
  const calloutId = doc.addBlock(
    'affine:callout' as never,
    {
      emoji: match.rule.emoji,
    },
    parentId,
    headingIndex
  );

  // Set the callout's title text via the rich-text insert API.
  const calloutBlock = doc.getBlock(calloutId)?.model as
    | ParagraphModelLike
    | undefined;
  if (calloutBlock && calloutBlock.text) {
    try {
      calloutBlock.text.insert(match.headingText, 0);
    } catch {
      /* ignore */
    }
  }

  // Re-attach the body as paragraph children of the callout. Empty
  // body is fine — the callout's title alone is still useful for TL;DR.
  for (const bodyText of bodyTexts) {
    const childId = doc.addBlock(
      'affine:paragraph' as never,
      { type: 'text' },
      calloutId
    );
    const childBlock = doc.getBlock(childId)?.model as
      | ParagraphModelLike
      | undefined;
    if (childBlock?.text) {
      try {
        childBlock.text.insert(bodyText, 0);
      } catch {
        /* ignore */
      }
    }
  }
}
